import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  ProcessDefinition,
  WorkflowApiService,
  WorkflowIntentDiscoveryCatalog,
  WorkflowIntentExecutionSourceMapping,
  WorkflowIntentGovernanceHealth,
  WorkflowIntentGovernancePolicyMismatch,
  WorkflowIntentTemplateCatalogItem
} from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-governance',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    TagModule
  ],
  template: `
    <ain-page [title]="t('workflow.intents.governance.title')" [description]="t('workflow.intents.governance.description')">
      <div class="toolbar">
        <input pInputText [(ngModel)]="moduleKey" [placeholder]="t('workflow.intents.governance.filters.moduleKey')" />
        <button pButton type="button" [label]="t('common.search')" (click)="reload()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else if (catalog()) {
        <p-card [header]="t('workflow.intents.governance.sections.health')">
          <div class="health-row">
            <span>{{ t('workflow.intents.governance.health.status') }}</span>
            <p-tag [value]="health()?.status || 'Unknown'" [severity]="resolveHealthSeverity(health()?.status)"></p-tag>
          </div>
          <div class="summary-line">{{ t('workflow.intents.governance.health.checkedAt') }}: {{ health()?.checkedAtUtc || '-' }}</div>
          <div class="summary-line">{{ t('workflow.intents.governance.health.mismatches') }}: {{ health()?.mismatchCount ?? 0 }}</div>
          <div class="summary-line">{{ t('workflow.intents.governance.health.validationIssues') }}: {{ health()?.validationIssueCount ?? 0 }}</div>
          <div class="summary-line">
            {{
              health()?.status?.toLowerCase() === 'healthy'
                ? t('workflow.intents.governance.health.healthySummary')
                : t('workflow.intents.governance.health.degradedSummary')
            }}
          </div>
          @if ((health()?.mismatches?.length ?? 0) > 0) {
            <p-table [value]="health()!.mismatches">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('columns.type') }}</th>
                  <th>{{ t('columns.name') }}</th>
                  <th>{{ t('workflow.intents.governance.columns.permission') }}</th>
                  <th>{{ t('columns.code') }}</th>
                  <th>{{ t('columns.description') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td>{{ item.sourceType }}</td>
                  <td>{{ item.sourceKey }}</td>
                  <td>{{ item.permission }}</td>
                  <td>{{ item.code }}</td>
                  <td>{{ t(item.code) }}</td>
                  <td class="actions">
                    <button
                      pButton
                      size="small"
                      text
                      severity="help"
                      [label]="t('workflow.intents.governance.actions.assignMapping')"
                      (click)="openExecutionSourceEditor(item)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>

            @if (selectedMismatch()) {
              <div class="mapping-editor">
                <h4>{{ t('workflow.intents.governance.sections.executionSourceEditor') }}</h4>
                <div class="summary-line">
                  {{ t('columns.type') }}: <strong>{{ selectedMismatch()!.sourceType }}</strong>
                  · {{ t('columns.name') }}: <strong>{{ selectedMismatch()!.sourceKey }}</strong>
                </div>
                <div class="summary-line">
                  {{ t('workflow.intents.governance.fields.currentStatus') }}:
                  <strong>{{ currentMappingStatusLabel() }}</strong>
                </div>
                <div class="editor-grid">
                  <div class="field">
                    <label>{{ t('workflow.intents.governance.fields.processDefinition') }}</label>
                    <p-select
                      [options]="processDefinitionOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="mappingEditor.processDefinitionId"
                      [showClear]="true"
                      appendTo="body" />
                  </div>
                  <div class="field">
                    <label>{{ t('workflow.intents.governance.fields.mappingStatus') }}</label>
                    <p-select
                      [options]="mappingStatusOptions"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="mappingEditor.status"
                      appendTo="body" />
                  </div>
                  <div class="field">
                    <label>{{ t('workflow.intents.governance.fields.validationCode') }}</label>
                    <input pInputText [(ngModel)]="mappingEditor.validationCode" />
                  </div>
                  <div class="field">
                    <label>{{ t('columns.description') }}</label>
                    <textarea pTextarea rows="2" [(ngModel)]="mappingEditor.notes"></textarea>
                  </div>
                </div>
                <div class="actions">
                  <button
                    pButton
                    type="button"
                    severity="secondary"
                    [label]="t('common.cancel')"
                    (click)="cancelExecutionSourceEditor()"></button>
                  <button
                    pButton
                    type="button"
                    [label]="t('common.save')"
                    [loading]="mappingSaveInProgress()"
                    (click)="saveExecutionSourceMapping()"></button>
                </div>
              </div>
            }
          }
        </p-card>

        <p-card [header]="t('workflow.intents.governance.sections.templates')">
          <p-table [value]="catalog()!.templates">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('workflow.intents.governance.columns.module') }}</th>
                <th>{{ t('workflow.intents.governance.columns.intent') }}</th>
                <th>{{ t('workflow.intents.governance.columns.template') }}</th>
                <th>{{ t('workflow.intents.governance.columns.version') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('workflow.intents.governance.columns.compatibility') }}</th>
                <th>{{ t('workflow.intents.governance.columns.owner') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.moduleKey }}</td>
                <td>{{ item.intentKey }}</td>
                <td>{{ item.templateKey }}</td>
                <td>{{ item.version }}</td>
                <td><p-tag [value]="item.status" [severity]="resolveStatusSeverity(item.status)"></p-tag></td>
                <td>{{ item.compatibilityScope }}</td>
                <td>{{ item.owner }}</td>
                <td class="actions">
                  <button pButton size="small" text [label]="t('workflow.intents.governance.actions.publish')" (click)="publish(item)"></button>
                  <button pButton size="small" text severity="secondary" [label]="t('workflow.intents.governance.actions.deprecate')" (click)="openDeprecate(item)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>

        <div class="grid">
          <p-card [header]="t('workflow.intents.governance.sections.validationIssues')">
            @if (catalog()!.issues.length === 0) {
              <ain-state type="empty" [title]="t('workflow.intents.governance.states.noIssues')" />
            } @else {
              <p-table [value]="catalog()!.issues">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('columns.code') }}</th>
                    <th>{{ t('columns.type') }}</th>
                    <th>{{ t('columns.name') }}</th>
                    <th>{{ t('columns.description') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-item>
                  <tr>
                    <td>{{ item.code }}</td>
                    <td>{{ item.sourceType }}</td>
                    <td>{{ item.key }}</td>
                    <td>{{ item.message }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          </p-card>

          <p-card [header]="t('workflow.intents.governance.sections.discoverySummary')">
            <div class="summary-line">{{ t('workflow.intents.governance.summary.intents') }}: {{ totals().intents }}</div>
            <div class="summary-line">{{ t('workflow.intents.governance.summary.templates') }}: {{ totals().templates }}</div>
            <div class="summary-line">{{ t('workflow.intents.governance.summary.hookActions') }}: {{ totals().hookActions }}</div>
            <div class="summary-line">{{ t('workflow.intents.governance.summary.moduleActions') }}: {{ totals().moduleActions }}</div>
            <div class="summary-line">{{ t('workflow.intents.governance.summary.issues') }}: {{ totals().issues }}</div>
          </p-card>
        </div>
      }
    </ain-page>

    @if (deprecateDialogVisible()) {
      <div class="deprecate-panel">
        <p-card [header]="t('workflow.intents.governance.actions.deprecate')">
          <div class="field">
            <label>{{ t('workflow.intents.governance.fields.deprecateReason') }}</label>
            <textarea pTextarea rows="3" [(ngModel)]="deprecateReason"></textarea>
          </div>
          <div class="actions">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="cancelDeprecation()"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="confirmDeprecation()"></button>
          </div>
        </p-card>
      </div>
    }
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
    .actions { display: flex; gap: 6px; justify-content: flex-end; }
    .grid { display: grid; gap: 12px; margin-top: 12px; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); }
    .summary-line { margin-bottom: 8px; }
    .health-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
    .mapping-editor { margin-top: 12px; border-top: 1px solid #e5e7eb; padding-top: 12px; }
    .editor-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); margin-bottom: 10px; }
    .deprecate-panel { margin-top: 12px; }
    .field { display: grid; gap: 6px; margin-bottom: 12px; }
  `]
})
export class WorkflowIntentGovernanceComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly catalog = signal<WorkflowIntentDiscoveryCatalog | null>(null);
  readonly health = signal<WorkflowIntentGovernanceHealth | null>(null);
  readonly deprecateDialogVisible = signal(false);
  readonly selectedTemplate = signal<WorkflowIntentTemplateCatalogItem | null>(null);
  readonly processDefinitions = signal<ProcessDefinition[]>([]);
  readonly executionSources = signal<WorkflowIntentExecutionSourceMapping[]>([]);
  readonly selectedMismatch = signal<WorkflowIntentGovernancePolicyMismatch | null>(null);
  readonly mappingSaveInProgress = signal(false);

  moduleKey = '';
  deprecateReason = '';
  mappingEditor = {
    processDefinitionId: null as string | null,
    status: 'Ready',
    validationCode: '',
    notes: ''
  };
  readonly mappingStatusOptions = [
    { label: 'Ready', value: 'Ready' },
    { label: 'Deferred', value: 'Deferred' }
  ];

  readonly totals = computed(() => {
    const data = this.catalog();
    return {
      intents: data?.intents.length ?? 0,
      templates: data?.templates.length ?? 0,
      hookActions: data?.hookActions.length ?? 0,
      moduleActions: data?.moduleActions.length ?? 0,
      issues: data?.issues.length ?? 0
    };
  });

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.loadExecutionSources();
    this.loadProcessDefinitions();
    this.api.getIntentDiscoveryCatalog(this.moduleKey || undefined).subscribe({
      next: catalog => {
        this.catalog.set(catalog);
        this.api.getIntentGovernanceHealth(true, this.moduleKey || undefined).subscribe({
          next: health => {
            this.health.set(health);
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.error.set(this.t('common.loadFailed'));
          }
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  publish(item: WorkflowIntentTemplateCatalogItem): void {
    this.api.publishIntentTemplate(item.templateKey).subscribe({
      next: () => this.reload(),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  processDefinitionOptions(): Array<{ label: string; value: string }> {
    return this.processDefinitions()
      .map(item => ({
        value: item.id,
        label: `${item.name} (${item.status})`
      }));
  }

  openExecutionSourceEditor(item: WorkflowIntentGovernancePolicyMismatch): void {
    this.selectedMismatch.set(item);
    const existing = this.executionSources().find(source =>
      source.sourceType === item.sourceType && source.sourceKey === item.sourceKey);
    this.mappingEditor = {
      processDefinitionId: existing?.processDefinitionId ?? null,
      status: existing?.status ?? 'Ready',
      validationCode: existing?.validationCode ?? '',
      notes: existing?.notes ?? ''
    };
  }

  cancelExecutionSourceEditor(): void {
    this.selectedMismatch.set(null);
    this.mappingEditor = {
      processDefinitionId: null,
      status: 'Ready',
      validationCode: '',
      notes: ''
    };
  }

  saveExecutionSourceMapping(): void {
    const mismatch = this.selectedMismatch();
    if (!mismatch) {
      return;
    }

    this.mappingSaveInProgress.set(true);
    this.api.upsertIntentExecutionSource(mismatch.sourceType, mismatch.sourceKey, {
      processDefinitionId: this.mappingEditor.processDefinitionId,
      status: this.mappingEditor.status,
      validationCode: this.mappingEditor.validationCode.trim() || null,
      notes: this.mappingEditor.notes.trim() || null
    }).subscribe({
      next: () => {
        this.mappingSaveInProgress.set(false);
        this.cancelExecutionSourceEditor();
        this.reload();
      },
      error: () => {
        this.mappingSaveInProgress.set(false);
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  currentMappingStatusLabel(): string {
    const mismatch = this.selectedMismatch();
    if (!mismatch) {
      return '-';
    }

    const existing = this.executionSources().find(source =>
      source.sourceType === mismatch.sourceType && source.sourceKey === mismatch.sourceKey);
    if (!existing) {
      return this.t('workflow.intents.governance.states.mappingNotFound');
    }

    const processId = existing.processDefinitionId ? ` · ${existing.processDefinitionId}` : '';
    return `${existing.status}${processId}`;
  }

  openDeprecate(item: WorkflowIntentTemplateCatalogItem): void {
    this.selectedTemplate.set(item);
    this.deprecateReason = '';
    this.deprecateDialogVisible.set(true);
  }

  cancelDeprecation(): void {
    this.selectedTemplate.set(null);
    this.deprecateDialogVisible.set(false);
    this.deprecateReason = '';
  }

  confirmDeprecation(): void {
    const selectedTemplate = this.selectedTemplate();
    if (!selectedTemplate) {
      return;
    }

    this.api.deprecateIntentTemplate(selectedTemplate.templateKey, this.deprecateReason || undefined).subscribe({
      next: () => {
        this.cancelDeprecation();
        this.reload();
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  resolveStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status.toLowerCase() === 'published') {
      return 'success';
    }
    if (status.toLowerCase() === 'deprecated') {
      return 'danger';
    }
    if (status.toLowerCase() === 'draft') {
      return 'warn';
    }
    return 'secondary';
  }

  resolveHealthSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!status) {
      return 'secondary';
    }

    if (status.toLowerCase() === 'healthy') {
      return 'success';
    }

    if (status.toLowerCase() === 'degraded') {
      return 'warn';
    }

    return 'secondary';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadExecutionSources(): void {
    this.api.listIntentExecutionSources().subscribe({
      next: items => this.executionSources.set(items),
      error: () => this.executionSources.set([])
    });
  }

  private loadProcessDefinitions(): void {
    this.api.listDefinitions().subscribe({
      next: items => this.processDefinitions.set(items),
      error: () => this.processDefinitions.set([])
    });
  }
}
